import { useState, useEffect, useCallback, useRef } from 'react';
import { ReactComponent as RefreshIcon }    from '../../../assets/icons/ArrowsCounterClockwise.svg';
import { ReactComponent as ExpandIcon }     from '../../../assets/icons/ArrowsOutIn.svg';
import { ReactComponent as TrayIcon }       from '../../../assets/icons/Tray.svg';
import { ReactComponent as ChevronDown }    from '../../../assets/icons/ChevronDown.svg';
import { ReactComponent as SearchIcon }     from '../../../assets/icons/MagnifyingGlass.svg';
import { ReactComponent as ComposeIcon }    from '../../../assets/icons/PencilSimpleLine.svg';
import { ReactComponent as FolderIcon }     from '../../../assets/icons/FolderSimplePlus.svg';
import { ReactComponent as StarIcon }       from '../../../assets/icons/Star.svg';
import { ReactComponent as PaperclipIcon }  from '../../../assets/Paperclip.svg';
import api from '../../../api';
import { getEcho } from '../../../echo';
import EmailComposer from '../../Email/EmailComposer';
import EmailDetailModal from './EmailDetailModal';
import { useI18n } from '../../../i18n';

const PER_PAGE = 15;

const toLocaleCode = (locale) => {
    if (locale === 'en') return 'en-GB';
    return locale || 'en';
};

const formatDate = (dateStr, locale) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return new Intl.DateTimeFormat(toLocaleCode(locale), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(d);
};

/* ── Skeleton rows ── */
const EmailSkeleton = () => (
    <>
        {[1, 2, 3, 4].map(i => (
            <tr key={i} className="email-skeleton-row">
                <td />
                <td><div className="email-skel-line" style={{ width: '70%' }} /></td>
                <td><div className="email-skel-line" style={{ width: '90%' }} /></td>
                <td><div className="email-skel-line" style={{ width: '55%', marginLeft: 'auto' }} /></td>
            </tr>
        ))}
    </>
);

/* ── FIX 3: Self-contained FolderDropdown — each header instance gets its own
   ref and open-state, so compact + expanded panels never share a single ref. ── */
const FolderDropdown = ({ folders, activeFolder, onFolderSelect, t }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="email-folder-dropdown" ref={ref}>
            <button
                className="email-inbox-badge"
                onClick={() => setOpen(v => !v)}
            >
                <TrayIcon className="email-badge-tray" />
                <span className="email-badge-text">{activeFolder?.name ?? t('pages.email_widget.inbox')}</span>
                <ChevronDown className="email-badge-chevron" />
            </button>

            {open && folders.length > 0 && (
                <div className="email-folder-list">
                    {folders.map(folder => (
                        <div
                            key={folder.id}
                            className={`email-folder-item ${activeFolder?.id === folder.id ? 'active' : ''}`}
                            onClick={() => { onFolderSelect(folder); setOpen(false); }}
                        >
                            <span className="email-folder-name">{folder.name}</span>
                            {folder.unread_count > 0 && (
                                <span className="email-folder-unread">{folder.unread_count}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const EmailWidget = () => {
    const { locale, t } = useI18n();
    const [accountId, setAccountId]           = useState(null);
    const [folders, setFolders]               = useState([]);
    const [activeFolder, setActiveFolder]     = useState(null);
    const [emails, setEmails]                 = useState([]);
    const [page, setPage]                     = useState(1);
    const [hasMore, setHasMore]               = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore]       = useState(false);
    const [isExpanded, setIsExpanded]         = useState(false);
    const [showComposer, setShowComposer]     = useState(false);
    const [showSearch, setShowSearch]         = useState(false);
    const [searchQuery, setSearchQuery]       = useState('');
    const [searchResults, setSearchResults]   = useState(null);
    const [isSearching, setIsSearching]       = useState(false);
    const [selectedIds, setSelectedIds]       = useState(new Set());
    const [isArchiving, setIsArchiving]       = useState(false);
    const [isSyncing, setIsSyncing]           = useState(false);
    const [openEmailId, setOpenEmailId]       = useState(null);

    const compactBodyRef  = useRef(null);
    const expandedBodyRef = useRef(null);
    const isFetching      = useRef(false);
    const searchInputRef  = useRef(null);
    const searchSeqRef    = useRef(0);
    // FIX 2: timeout fallback so isSyncing never spins forever if Pusher is absent
    const syncTimeoutRef  = useRef(null);

    /* ── Derived display state ── */
    const inSearchMode  = showSearch && searchQuery.length >= 2;
    const displayEmails = inSearchMode ? (searchResults ?? []) : emails;
    const showSkeleton  = initialLoading || (inSearchMode && isSearching && searchResults === null);

    const fetchEmails = useCallback(async (folderId, pageNum, reset = false) => {
        if (isFetching.current || !folderId) return;
        isFetching.current = true;

        if (pageNum === 1) setInitialLoading(true);
        else setLoadingMore(true);

        try {
            const res = await api.get(`/mail/folders/${folderId}/emails?page=${pageNum}&per_page=${PER_PAGE}`);
            if (res.status) {
                const { emails: newEmails, pagination } = res.data;
                setEmails(prev => (reset || pageNum === 1) ? newEmails : [...prev, ...newEmails]);
                setHasMore(pagination.has_more);
                setPage(pageNum);
            }
        } catch (e) {
            // interceptor handles error toast
        } finally {
            setInitialLoading(false);
            setLoadingMore(false);
            isFetching.current = false;
        }
    }, []);

    // Init: accounts → default → folders → inbox
    useEffect(() => {
        const init = async () => {
            try {
                const accRes = await api.get('/mail/accounts');
                if (!accRes.status || !accRes.data.accounts?.length) {
                    setInitialLoading(false);
                    return;
                }
                const acct = accRes.data.accounts.find(a => a.is_default) ?? accRes.data.accounts[0];
                setAccountId(acct.id);

                const folRes = await api.get(`/mail/accounts/${acct.id}/folders`);
                if (folRes.status && folRes.data.folders?.length) {
                    const list = folRes.data.folders;
                    setFolders(list);
                    const inbox = list.find(f => f.type === 'inbox') ?? list[0];
                    setActiveFolder({
                        id: inbox.id,
                        name: inbox.name || t('pages.email_widget.inbox'),
                        type: inbox.type,
                    });
                    await fetchEmails(inbox.id, 1, true);
                } else {
                    setInitialLoading(false);
                }
            } catch (e) {
                setInitialLoading(false);
            }
        };
        init();
    }, [fetchEmails, t]);

    // FIX 8: Debounced search — scoped to the active folder
    useEffect(() => {
        if (!accountId || searchQuery.length < 2) {
            setSearchResults(null);
            setIsSearching(false);
            return;
        }
        const seq = ++searchSeqRef.current;
        setIsSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await api.get(`/mail/accounts/${accountId}/search`, {
                    params: {
                        q: searchQuery,
                        ...(activeFolder?.id ? { folder_id: activeFolder.id } : {}),
                    },
                });
                if (seq === searchSeqRef.current && res.status) {
                    setSearchResults(res.data.emails ?? []);
                }
            } catch {
                // interceptor toasts the error; show empty results
                if (seq === searchSeqRef.current) setSearchResults([]);
            } finally {
                if (seq === searchSeqRef.current) setIsSearching(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery, accountId, activeFolder]);

    // Clear selection when folder or search mode changes
    useEffect(() => { setSelectedIds(new Set()); }, [activeFolder, showSearch]);

    const closeSearch = useCallback(() => {
        setShowSearch(false);
        setSearchQuery('');
        setSearchResults(null);
        setIsSearching(false);
    }, []);

    const handleFolderSelect = useCallback((folder) => {
        setActiveFolder({ id: folder.id, name: folder.name, type: folder.type });
        fetchEmails(folder.id, 1, true);
    }, [fetchEmails]);

    // FIX 7: Removed isFetching.current from the guard — it's already checked
    // inside fetchEmails itself, so it was blocking load-more in the expanded
    // panel while the compact panel was fetching (they share the same ref).
    const makeScrollHandler = useCallback((ref) => () => {
        const el = ref.current;
        if (!el || loadingMore || !hasMore || !activeFolder || inSearchMode) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
            fetchEmails(activeFolder.id, page + 1);
        }
    }, [fetchEmails, hasMore, loadingMore, page, activeFolder, inSearchMode]);

    /* ── Selection ── */
    const toggleSelect = useCallback((id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds(prev =>
            prev.size === displayEmails.length
                ? new Set()
                : new Set(displayEmails.map(e => e.id))
        );
    }, [displayEmails]);

    /* ── Folder refresh ── */
    const refreshFolders = useCallback(async () => {
        if (!accountId) return;
        try {
            const res = await api.get(`/mail/accounts/${accountId}/folders`);
            if (res.status && res.data.folders) setFolders(res.data.folders);
        } catch {}
    }, [accountId]);

    /* ── FIX 2: Sync — isSyncing resets on HTTP error and has a 30 s fallback
       in case the Pusher .mail.sync.completed event never fires. ── */
    const handleSync = useCallback(async () => {
        if (!accountId || isSyncing) return;
        setIsSyncing(true);
        // Fallback: always reset after 30 s so the button never spins forever
        syncTimeoutRef.current = setTimeout(() => setIsSyncing(false), 30_000);
        try {
            const res = await api.post(`/mail/accounts/${accountId}/sync`);
            const syncError = res?.data?.sync_error;
            if (syncError) {
                clearTimeout(syncTimeoutRef.current);
                setIsSyncing(false);
            } else {
                await refreshFolders();
                // isSyncing stays true — Pusher fires .mail.sync.completed when done
            }
        } catch {
            // interceptor handles error toast; reset so button is usable again
            clearTimeout(syncTimeoutRef.current);
            setIsSyncing(false);
        }
    }, [accountId, isSyncing, refreshFolders]);

    // Pusher: listen for async sync completion
    useEffect(() => {
        if (!accountId) return;
        const echo = getEcho();
        if (!echo) return;
        const channel = echo.private(`mail.${accountId}`);
        channel.listen('.mail.sync.completed', (e) => {
            // FIX 2: clear the fallback timeout when Pusher fires normally
            clearTimeout(syncTimeoutRef.current);
            setIsSyncing(false);
            if (e.folders) {
                setFolders(prev => prev.map(folder => {
                    const updated = e.folders.find(f => f.id === folder.id);
                    return updated ? { ...folder, ...updated } : folder;
                }));
            }
            if (activeFolder) fetchEmails(activeFolder.id, 1, true);
        });
        return () => {
            channel.stopListening('.mail.sync.completed');
            clearTimeout(syncTimeoutRef.current);
            // Do NOT call echo.leave() — EmailProvider owns the channel lifecycle
        };
    }, [accountId, activeFolder, fetchEmails]);

    /* ── Archive ── */
    const handleArchive = useCallback(async () => {
        if (!accountId || selectedIds.size === 0 || isArchiving) return;
        setIsArchiving(true);
        try {
            const res = await api.post(`/mail/accounts/${accountId}/archive`, {
                email_ids: [...selectedIds],
            });
            if (res.status) {
                const archivedSet = new Set(res.data.archived_ids);
                setEmails(prev => prev.filter(e => !archivedSet.has(e.id)));
                setSelectedIds(new Set());
                await refreshFolders();
            }
        } catch {
            // interceptor handles error toast
        } finally {
            setIsArchiving(false);
        }
    }, [accountId, selectedIds, isArchiving, refreshFolders]);

    const isArchiveFolder = activeFolder?.type === 'archive';

    const handleUnarchive = useCallback(async () => {
        if (!accountId || selectedIds.size === 0 || isArchiving) return;
        setIsArchiving(true);
        try {
            const res = await api.post(`/mail/accounts/${accountId}/unarchive`, {
                email_ids: [...selectedIds],
            });
            if (res.status) {
                const unarchivedSet = new Set(res.data.unarchived_ids);
                setEmails(prev => prev.filter(e => !unarchivedSet.has(e.id)));
                setSelectedIds(new Set());
                await refreshFolders();
            }
        } catch {
            // interceptor handles error toast
        } finally {
            setIsArchiving(false);
        }
    }, [accountId, selectedIds, isArchiving, refreshFolders]);

    /* ── FIX 5: Star toggle — optimistic update, reverts on error ── */
    const handleToggleStar = useCallback(async (emailId, currentStarred) => {
        setEmails(prev => prev.map(e =>
            e.id === emailId ? { ...e, is_starred: !currentStarred } : e
        ));
        try {
            await api.post(`/mail/emails/${emailId}/star`);
        } catch {
            // Revert on error
            setEmails(prev => prev.map(e =>
                e.id === emailId ? { ...e, is_starred: currentStarred } : e
            ));
        }
    }, []);

    /* ── FIX 4: Open email — optimistically mark as read in local list so
       the unread indicator clears immediately without waiting for the modal. ── */
    const handleOpenEmail = useCallback((emailId, isRead) => {
        setOpenEmailId(emailId);
        if (!isRead) {
            setEmails(prev => prev.map(e =>
                e.id === emailId ? { ...e, is_read: true } : e
            ));
        }
    }, []);

    /* ── Shared render helpers ── */

    const renderHeader = (onExpand) => (
        <div className="widget-header email-widget-header">
            <div className="email-header-left">
                <span className="widget-title email-widget-title">{t('navigation.email')}</span>

                {/* FIX 3: FolderDropdown is its own component — each header
                    call gets an independent instance with its own ref/state,
                    so compact + expanded panels no longer conflict. */}
                <FolderDropdown
                    folders={folders}
                    activeFolder={activeFolder}
                    onFolderSelect={handleFolderSelect}
                    t={t}
                />
            </div>

            <div className="header-right">
                <SearchIcon
                    className={`widget-icon email-action-icon ${showSearch ? 'active' : ''}`}
                    onClick={() => {
                        if (showSearch) {
                            closeSearch();
                        } else {
                            setShowSearch(true);
                            setTimeout(() => searchInputRef.current?.focus(), 50);
                        }
                    }}
                    title={t('common.actions.search')}
                />
                <ComposeIcon className="widget-icon email-action-icon" onClick={() => setShowComposer(true)} title={t('common.actions.create')} />
                <FolderIcon
                    className={`widget-icon email-action-icon ${selectedIds.size === 0 ? 'disabled' : ''}`}
                    onClick={selectedIds.size > 0 ? (isArchiveFolder ? handleUnarchive : handleArchive) : undefined}
                    title={
                        selectedIds.size === 0
                            ? (isArchiveFolder ? t('common.actions.restore') : t('common.actions.archive'))
                            : (isArchiveFolder ? t('common.actions.restore') : t('common.actions.archive'))
                    }
                />
                <RefreshIcon
                    className={`widget-icon email-action-icon ${isSyncing ? 'email-syncing' : ''}`}
                    onClick={handleSync}
                    title={isSyncing ? t('common.status.syncing') : t('legacy.sync_new_emails')}
                />
                <ExpandIcon className="widget-icon email-action-icon" onClick={onExpand} title={isExpanded ? t('common.actions.collapse') : t('common.actions.expand')} />
            </div>
        </div>
    );

    const renderSearchBar = () => (
        <div className="email-search-bar">
            <SearchIcon className="email-search-icon-sm" />
            <input
                ref={searchInputRef}
                className="email-search-input"
                placeholder={t('legacy.search_emails')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
            />
            {isSearching && <div className="email-search-spinner" />}
            <button className="email-search-close" onClick={closeSearch}>✕</button>
        </div>
    );

    const renderBody = (ref, onScroll) => (
        <div className="widget-body email-widget-body" ref={ref} onScroll={onScroll}>
            <table className="email-list-table">
                <thead>
                    <tr>
                        <th className="email-th email-th-cb">
                            <input
                                type="checkbox"
                                className="email-row-checkbox"
                                checked={displayEmails.length > 0 && selectedIds.size === displayEmails.length}
                                onChange={toggleSelectAll}
                            />
                        </th>
                        <th className="email-th">{t('legacy.from_header')}</th>
                        <th className="email-th">{t('legacy.subject_header')}</th>
                        <th className="email-th email-th-date">{t('common.labels.date')}</th>
                    </tr>
                </thead>
                <tbody>
                    {showSkeleton ? (
                        <EmailSkeleton />
                    ) : displayEmails.length === 0 ? (
                        <tr>
                            <td colSpan={4} className="email-empty">
                                {inSearchMode ? t('common.empty_states.no_results') : t('tables.empty.no_emails')}
                            </td>
                        </tr>
                    ) : (
                        displayEmails.map((email, i) => (
                            <tr
                                key={email.id ?? i}
                                className={`email-row ${!email.is_read ? 'unread' : ''} ${selectedIds.has(email.id) ? 'selected' : ''}`}
                                onClick={() => handleOpenEmail(email.id, email.is_read)}
                            >
                                <td className="email-td email-td-cb" onClick={e => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        className="email-row-checkbox"
                                        checked={selectedIds.has(email.id)}
                                        onChange={() => toggleSelect(email.id)}
                                    />
                                </td>
                                <td className="email-td email-td-from">
                                    <div className="email-from-cell">
                                        {/* FIX 5: star icon is now interactive */}
                                        <StarIcon
                                            className={`email-star-icon ${email.is_starred ? 'starred' : ''}`}
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleToggleStar(email.id, email.is_starred);
                                            }}
                                        />
                                        <span className="email-sender-name">
                                            {email.from?.name || email.from?.email || t('common.misc.unknown')}
                                        </span>
                                    </div>
                                </td>
                                <td className="email-td email-td-subject">
                                    <div className="email-subject-cell">
                                        {email.has_attachments && (
                                            <PaperclipIcon className="email-attach-icon" />
                                        )}
                                        <span className="email-subject-text">{email.subject}</span>
                                    </div>
                                </td>
                                <td className="email-td email-td-date">
                                    {formatDate(email.date, locale)}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {!showSkeleton && !inSearchMode && loadingMore && (
                <div className="nfw-load-more-spinner"><div className="nfw-spinner" /></div>
            )}
            {!showSkeleton && !inSearchMode && !hasMore && displayEmails.length > 0 && (
                <div className="nfw-end-of-feed">{t('legacy.no_more_emails')}</div>
            )}
        </div>
    );

    return (
        <>
            {/* ── Compact widget ── */}
            <div className="widget-card email-widget">
                {renderHeader(() => setIsExpanded(true))}
                {showSearch && renderSearchBar()}
                {renderBody(compactBodyRef, makeScrollHandler(compactBodyRef))}
            </div>

            {/* ── Expanded panel ── */}
            {isExpanded && (
                <div className="nfw-expanded-overlay" onClick={() => setIsExpanded(false)}>
                    <div className="nfw-expanded-panel" onClick={e => e.stopPropagation()}>
                        {renderHeader(() => setIsExpanded(false))}
                        {showSearch && renderSearchBar()}
                        {renderBody(expandedBodyRef, makeScrollHandler(expandedBodyRef))}
                    </div>
                </div>
            )}

            {/* ── Compose modal ── */}
            <EmailComposer
                isOpen={showComposer}
                onClose={() => setShowComposer(false)}
                mode="compose"
                originalEmail={null}
            />

            {/* ── Email detail modal ── */}
            <EmailDetailModal
                emailId={openEmailId}
                onClose={() => setOpenEmailId(null)}
            />
        </>
    );
};

export default EmailWidget;
